import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, FileSpreadsheet, Upload, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { KpiCard } from "@/components/KpiCard";
import { SimDialog } from "@/components/phones/SimDialog";
import { SimImportDialog } from "@/components/phones/SimImportDialog";
import { download } from "@/components/phones/PhoneRegister";
import { api, errMsg } from "@/lib/api";
import { useSort, SortTh } from "@/lib/sort";

const FILTERS = [["all", "Tutte"], ["linked", "Associate"], ["free", "Libere"]];
const Code = ({ v, show }) => <span className="font-mono-num">{v ? (show ? v : "••••") : "—"}</span>;

export const SimsPanel = ({ sid, stations, isAdmin, onChanged }) => {
  const [data, setData] = useState({ items: [], summary: {} });
  const [phones, setPhones] = useState([]);
  const [editing, setEditing] = useState(null);
  const [imp, setImp] = useState(false);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [show, setShow] = useState(false);
  const load = useCallback(() => {
    api.get("/phones/sims", { params: { station_id: sid } }).then((r) => setData(r.data));
    api.get("/phones").then((r) => setPhones(r.data.items));
  }, [sid]);
  useEffect(() => { load(); }, [load]);
  const done = () => { load(); onChanged(); };

  const items = useMemo(() => {
    const t = q.trim().toLowerCase().replace(/\s/g, "");
    return data.items.filter((i) => (filter === "all" || (filter === "linked") === !!i.phone)
      && (!t || [i.serial, i.internal_number, i.phone?.imei, i.phone?.label, i.phone?.phone_number, i.station_name].some((x) => (x || "").toLowerCase().replace(/\s/g, "").includes(t))));
  }, [data.items, q, filter]);
  const { sorted, sort, toggle } = useSort(items, { phone: (i) => i.phone?.imei || "" });
  const S = (p) => <SortTh sort={sort} onSort={toggle} {...p} />;
  const remove = async (i) => {
    if (!window.confirm(`Eliminare la SIM ${i.serial}?`)) return;
    try { await api.delete(`/phones/sims/${i.id}`); toast.success("SIM eliminata"); done(); } catch (e) { toast.error(errMsg(e)); }
  };
  const s = data.summary;

  return (
    <div data-testid="sims-panel">
      <div className="mb-4 flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => setImp(true)} data-testid="sim-import-button"><Upload className="mr-2 h-4 w-4" /> Importa Excel</Button>
        <Button variant="outline" onClick={() => download("/phones/sims/export", { station_id: sid }, "sim.xlsx")} data-testid="sim-export-button"><FileSpreadsheet className="mr-2 h-4 w-4" /> Scarica Excel</Button>
        <Button onClick={() => setEditing({ station_id: sid || "" })} data-testid="sim-add-button"><Plus className="mr-2 h-4 w-4" /> Nuova SIM</Button>
      </div>
      <div className="mb-6 grid grid-cols-3 gap-4">
        <KpiCard label="SIM totali" value={s.total ?? 0} accent="#0F172A" testId="sim-kpi-total" />
        <KpiCard label="Associate" value={s.linked ?? 0} accent="#0284C7" testId="sim-kpi-linked" />
        <KpiCard label="Libere" value={s.free ?? 0} accent="#059669" testId="sim-kpi-free" />
      </div>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1" data-testid="sim-filters">
          {FILTERS.map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`sim-filter-${k}`}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${filter === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{l}</button>
          ))}
        </div>
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca seriale, numero interno, IMEI..." className="pl-9" data-testid="sim-search-input" />
        </div>
        <Button variant="ghost" size="sm" onClick={() => setShow((x) => !x)} data-testid="sim-toggle-codes">{show ? <EyeOff className="mr-1 h-4 w-4" /> : <Eye className="mr-1 h-4 w-4" />}{show ? "Nascondi PIN/PUK" : "Mostra PIN/PUK"}</Button>
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="sim-table">
            <thead className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500 [&_th]:whitespace-nowrap">
              <tr><S k="serial">Seriale SIM</S><S k="internal_number">N. interno</S><S k="received_date">Arrivo</S><th className="px-3 py-2">PIN</th><th className="px-3 py-2">PUK</th>
                <S k="station_name">Stazione</S><S k="phone">Telefono associato</S><th className="px-3 py-2" /></tr>
            </thead>
            <tbody className="divide-y">
              {sorted.map((i) => (
                <tr key={i.id} className="hover:bg-slate-50" data-testid={`sim-row-${i.id}`}>
                  <td className="font-mono-num whitespace-nowrap px-3 py-2 font-semibold">{i.serial}</td>
                  <td className="whitespace-nowrap px-3 py-2" data-testid={`sim-internal-${i.id}`}>{i.internal_number || "—"}</td>
                  <td className="whitespace-nowrap px-3 py-2">{i.received_date ? new Date(i.received_date).toLocaleDateString("it-IT") : "—"}</td>
                  <td className="whitespace-nowrap px-3 py-2" data-testid={`sim-pin-${i.id}`}><Code v={i.pin} show={show} /></td>
                  <td className="whitespace-nowrap px-3 py-2" data-testid={`sim-puk-${i.id}`}><Code v={i.puk} show={show} /></td>
                  <td className="whitespace-nowrap px-3 py-2">{i.station_name}</td>
                  <td className="whitespace-nowrap px-3 py-2" data-testid={`sim-phone-${i.id}`}>{i.phone ? <><span className="font-mono-num">{i.phone.imei}</span> <span className="text-xs text-slate-500">{i.phone.label}</span></> : <span className="text-slate-400">Libera</span>}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(i)} data-testid={`sim-edit-${i.id}`}><Pencil className="h-4 w-4" /></Button>
                    {isAdmin && <Button size="sm" variant="ghost" className="text-slate-400" onClick={() => remove(i)} data-testid={`sim-delete-${i.id}`}><Trash2 className="h-4 w-4" /></Button>}
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={8} className="px-3 py-10 text-center text-slate-500" data-testid="sim-empty">{q || filter !== "all" ? "Nessuna SIM corrisponde ai filtri." : "Nessuna SIM registrata."}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <SimDialog item={editing} stations={stations} phones={phones} isAdmin={isAdmin} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); done(); }} />
      <SimImportDialog key={String(imp)} open={imp} stations={stations} isAdmin={isAdmin} defaultStation={sid} onClose={() => setImp(false)} onDone={() => { setImp(false); done(); }} />
    </div>
  );
};
